// Phoneware activity (issue #1771, mock M1 admin-tiers.html): every time someone at
// Phoneware acted as a dealer user, and every change they made, read from
// GET /api/activity. Settings shows the sessions; quote, order and customer screens show
// the changes that touched them; a dealer's Today says when Phoneware last acted there;
// and the dialog that says Omni ended a session. Dealers read "Phoneware" where the
// admin's name would be: the server sends it that way.
import React, { useState } from "react";
import {
  ACT_AS_IDLE_MINUTES,
  roleLabel,
  type ActAsEndReason,
  type ActivityScope,
  type ActivitySession,
  type AuditAction,
  type AuditEntity,
  type AuditEntityKind,
} from "@phoneware/lens-domain";
import type { ActAsEnded } from "../api.js";
import { Icon } from "../icons.js";
import { Link, navigate } from "../router.js";
import { useLoad, useSession, viewerKind } from "../session.js";
import { clock, firstName, initials, plural } from "../format.js";
import { Chip } from "../ui.js";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Today", "Yesterday", or "Oct 15". */
function dayOf(iso: string, now = new Date()): string {
  const d = new Date(iso);
  if (d.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`;
}

/** Mid-sentence: "today", "yesterday", "on Oct 15". */
function dayIn(iso: string): string {
  const day = dayOf(iso);
  return day === "Today" ? "today" : day === "Yesterday" ? "yesterday" : `on ${day}`;
}

/** "today at 10:10 AM", "Oct 15 at 11:05 AM". */
function dayAt(iso: string): string {
  const day = dayOf(iso);
  return `${day === "Today" || day === "Yesterday" ? day.toLowerCase() : day} at ${clock(new Date(iso))}`;
}

/** How a session ended, in the mock's words. */
export const END_WORD: Record<ActAsEndReason, string> = {
  stopped: "Stopped",
  idle: `Ended after ${ACT_AS_IDLE_MINUTES} minutes idle`,
  signed_out: "Ended at sign-out",
  replaced: "Replaced by a new session",
  admin_lost_permission: "Ended: the admin lost act-as",
  target_disabled: "Ended: the person was turned off",
};

/** Where a change can be opened, when its record has a screen. */
function entityPath(entity: AuditEntity | null): string | null {
  if (!entity) return null;
  switch (entity.kind) {
    case "quote":
      return `/quotes/${encodeURIComponent(entity.id)}`;
    case "order":
      return `/orders/${encodeURIComponent(entity.id)}`;
    case "customer":
      return `/customers/${encodeURIComponent(entity.id)}`;
    default:
      return null;
  }
}

/** A change as its own record says it: "sent this quote". The rest read as the log's summary. */
const ON_RECORD: Partial<Record<AuditAction, string>> = {
  "quotes.create": "created this quote",
  "quotes.update": "changed this quote",
  "quotes.send": "sent this quote",
  "quotes.clone": "cloned this quote",
  "quotes.mark_sold": "marked this quote sold",
  "quotes.mark_lost": "marked this quote lost",
  "quotes.approval_decision": "decided the approval on this quote",
  "quotes.delete": "deleted this quote",
  "orders.update": "changed this order",
  "orders.retry": "retried this order",
  "orders.approve_billing": "approved billing on this order",
  "customers.create": "added this customer",
  "customers.add_contact": "added a contact to this customer",
  "customers.assign": "assigned this customer",
  "customers.update_billing": "changed this customer\u2019s billing",
  "customers.update_category": "changed this customer\u2019s category",
  "customers.update_account_number": "changed this customer\u2019s account number",
};

function onRecord(action: AuditAction, summary: string): string {
  return ON_RECORD[action] ?? `${summary.charAt(0).toLowerCase()}${summary.slice(1)}`;
}

/** Who acted: the admin's initials for Phoneware, PW for a dealer, who never sees the name. */
function ActorMark({ s }: { s: ActivitySession }): React.ReactElement {
  return s.admin.email ? (
    <span className="av azure" aria-hidden="true">
      {initials(s.admin.name)}
    </span>
  ) : (
    <span className="av sky" title="Phoneware" aria-hidden="true">
      PW
    </span>
  );
}

function lead(scope: ActivityScope, dealerName: string): string {
  switch (scope) {
    case "all_sessions":
      return "Every time someone at Phoneware acted as a dealer user, and every change they made. Dealers see these with Phoneware in place of the name.";
    case "dealer_sessions":
      return `Every time someone at Phoneware acted as a person at ${dealerName}, and every change they made.`;
    case "own_sessions":
      return "Every time someone at Phoneware acted as you, and every change they made.";
    default: {
      const unhandled: never = scope;
      throw new Error(`Unknown activity scope ${String(unhandled)}`);
    }
  }
}

function nobody(scope: ActivityScope): string {
  switch (scope) {
    case "all_sessions":
      return "Nobody at Phoneware has acted as a dealer user yet.";
    case "dealer_sessions":
      return "Nobody at Phoneware has acted as anyone here.";
    case "own_sessions":
      return "Nobody at Phoneware has acted as you.";
    default: {
      const unhandled: never = scope;
      throw new Error(`Unknown activity scope ${String(unhandled)}`);
    }
  }
}

/** Settings, Phoneware activity: the sessions in the caller's scope, newest first, the three newest open. */
export function ActivityTab(): React.ReactElement {
  const { me, api } = useSession();
  const view = useLoad(() => api.activity(), []);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  if (!view.data) {
    return (
      <section className="card" data-testid="activity">
        <div className="card-h">
          <h2>Phoneware activity</h2>
        </div>
        <div className="empty">{view.error ? view.error.message : "Loading Phoneware activity."}</div>
      </section>
    );
  }
  const { scope, sessions } = view.data;
  const list = [...sessions].sort((a, b) => b.startedAt.localeCompare(a.startedAt));
  const isOpen = (s: ActivitySession, i: number) => open[s.id] ?? i < 3;
  return (
    <section className="card" data-testid="activity">
      <div className="card-h">
        <h2>Phoneware activity</h2>
        <span className="note">{plural(list.length, "session")}</span>
      </div>
      <p className="card-p lead">{lead(scope, me.dealer?.name ?? "your dealer")}</p>
      {list.length ? (
        <ul className="sessions">
          {list.map((s, i) => {
            const shown = isOpen(s, i);
            const you = s.target.email === me.user.email;
            const span = `${dayOf(s.startedAt)}, ${clock(new Date(s.startedAt))}${s.endedAt ? ` to ${clock(new Date(s.endedAt))}` : ""}`;
            const where =
              scope === "all_sessions" ? `${roleLabel(s.target.role)}, ${s.dealer.name} \u00b7 ${span}` : you ? span : `${roleLabel(s.target.role)} \u00b7 ${span}`;
            return (
              <li key={s.id} data-testid={`session-${s.id}`}>
                <button className="ses" type="button" aria-expanded={shown} onClick={() => setOpen({ ...open, [s.id]: !shown })}>
                  <ActorMark s={s} />
                  <span className="row-main">
                    <span className="row-t">
                      {s.admin.name} acted as {you ? "you" : s.target.name}
                    </span>
                    <span className="row-m">{where}</span>
                    <span className="ses-chips">
                      {s.endedAt && s.endReason ? (
                        <Chip kind={s.endReason === "idle" ? "quiet" : ""} icon={s.endReason === "idle" ? "clock" : "check"}>
                          {END_WORD[s.endReason]}
                        </Chip>
                      ) : (
                        <Chip kind="warn" icon="user">
                          Now
                        </Chip>
                      )}
                      <Chip kind={s.changes.length ? "" : "quiet"}>{plural(s.changes.length, "change")}</Chip>
                    </span>
                  </span>
                  <Icon name={shown ? "chevron-down" : "chevron-right"} size={16} className="chev" />
                </button>
                {shown ? (
                  s.changes.length ? (
                    <ol className="ses-changes">
                      {s.changes.map((c, n) => {
                        const to = entityPath(c.entity);
                        return (
                          <li key={n}>
                            <time dateTime={c.at}>{clock(new Date(c.at))}</time>
                            <span>
                              {c.summary}
                              {to ? (
                                <>
                                  {" "}
                                  <Link to={to}>Open</Link>
                                </>
                              ) : null}
                            </span>
                          </li>
                        );
                      })}
                    </ol>
                  ) : (
                    <p className="ses-none">No changes.</p>
                  )
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="empty">{nobody(scope)}</div>
      )}
    </section>
  );
}

/**
 * The changes Phoneware made to one record while acting as someone, newest first: "Phoneware, acting as Marisol
 * Quintero, sent this quote". Nothing shows until a session touched the record.
 */
export function RecordActivity({ kind, id, version }: { kind: AuditEntityKind; id: string; version?: unknown }): React.ReactElement | null {
  const { me, api } = useSession();
  const view = useLoad(() => api.activity({ kind, id }), [kind, id, version]);
  const lines = (view.data?.sessions ?? [])
    .flatMap((s) => s.changes.map((c) => ({ s, c })))
    .sort((a, b) => b.c.at.localeCompare(a.c.at));
  if (!lines.length) return null;
  const viewer = viewerKind(me.user);
  let note: string;
  switch (viewer) {
    case "admin":
      note = "Names as Phoneware sees them";
      break;
    case "dealer":
      note = `As ${me.dealer?.name ?? "your dealer"} sees it`;
      break;
    default: {
      const unhandled: never = viewer;
      throw new Error(`Unknown viewer ${String(unhandled)}`);
    }
  }
  return (
    <section className="card" data-testid={`${kind}-activity`}>
      <div className="card-h">
        <h2>Activity</h2>
        <span className="note">{note}</span>
      </div>
      <ul className="alog">
        {lines.map(({ s, c }, n) => (
          <li key={`${s.id}-${n}`} className="as">
            <span className="ai" aria-hidden="true">
              <Icon name="user" size={13} />
            </span>
            <span className="al">
              <span data-testid="log-line">
                <b>{s.admin.name}</b>, acting as {s.target.name}, {onRecord(c.action, c.summary)}
              </span>
              <span className="sub">{dayAt(c.at)}</span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** A dealer's Today: when Phoneware last acted here and changed something, with the way to see it. */
export function ActedCallout(): React.ReactElement | null {
  const { me, api } = useSession();
  const view = useLoad(() => api.activity(), []);
  const recent = (view.data?.sessions ?? [])
    .filter((s) => s.endedAt && s.changes.length)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0];
  if (!recent) return null;
  return (
    <div className="callout acted" data-testid="today-activity">
      <Icon name="history" size={15} />
      <div>
        {recent.admin.name} acted as {recent.target.email === me.user.email ? "you" : recent.target.name} {dayIn(recent.startedAt)} and made{" "}
        {plural(recent.changes.length, "change")}. <Link to="/settings/activity">See Phoneware activity</Link>
      </div>
    </div>
  );
}

function endedLead(reason: ActAsEndReason | null, name: string): string {
  if (reason === null) return `Omni has no open session acting as ${name}.`;
  switch (reason) {
    case "idle":
      return `Omni stopped acting as ${name} after ${ACT_AS_IDLE_MINUTES} minutes with nothing done.`;
    case "replaced":
      return `Omni stopped acting as ${name} because you started acting as someone else.`;
    case "admin_lost_permission":
      return `Omni stopped acting as ${name} because your tier no longer allows it.`;
    case "target_disabled":
      return `Omni stopped acting as ${name} because ${firstName(name)} was turned off.`;
    case "stopped":
      return `Acting as ${name} was already stopped.`;
    case "signed_out":
      return `Acting as ${name} ended at sign-out.`;
    default: {
      const unhandled: never = reason;
      throw new Error(`Unknown end reason ${String(unhandled)}`);
    }
  }
}

/** Omni ended this tab's session under it (mock M1 state 60). Shown over the admin's own Home once the log is read. */
export function ActAsEndedDialog({ ended, onClose }: { ended: ActAsEnded; onClose: () => void }): React.ReactElement | null {
  const { api } = useSession();
  const view = useLoad(() => api.activity(), [ended.sessionId]);
  if (view.loading && !view.data) return null;
  const s = view.data?.sessions.find((x) => x.id === ended.sessionId) ?? null;
  const go = (to: string) => {
    onClose();
    navigate(to);
  };
  const name = s?.target.name ?? null;
  const first = name ? firstName(name) : null;
  const n = s?.changes.length ?? 0;
  return (
    <>
      <div className="scrim show" />
      <div className="mac-dialog ended" role="alertdialog" aria-modal="true" aria-labelledby="ended-h" data-testid="ended">
        <div className="dlg-h">
          <span className="ended-ic" aria-hidden="true">
            <Icon name="clock" size={18} />
          </span>
          <h2 id="ended-h">Acting as ended</h2>
        </div>
        <div className="dlg-b">
          <p className="lead">{name ? endedLead(ended.reason, name) : "Omni is no longer acting as anyone in this tab."}</p>
          {s && first ? (
            <p className="dlg-note">
              {n
                ? `${plural(n, "change")} from this session ${n === 1 ? "is" : "are"} saved and recorded as ${s.admin.name} acting as ${first}. `
                : "Nothing changed in this session. "}
              Nothing you do now is recorded as {first}. To keep going, act as {first} again.
            </p>
          ) : (
            <p className="dlg-note">Nothing you do now is recorded as someone else.</p>
          )}
        </div>
        <div className="dlg-f">
          <button className="btn" type="button" data-testid="ended-activity" onClick={() => go("/settings/activity")}>
            See the session
          </button>
          {s ? (
            <button className="btn primary" type="button" data-testid="ended-back" onClick={() => go(`/dealers?d=${encodeURIComponent(s.dealer.id)}`)}>
              Back to {s.dealer.name}
            </button>
          ) : (
            <button className="btn primary" type="button" data-testid="ended-back" onClick={onClose}>
              Back to Home
            </button>
          )}
        </div>
      </div>
    </>
  );
}
